/**
 * The bridge from an `ExternalStore` to a component.
 *
 * It lives in `app/` and not in `stores/` because `stores/` imports no React —
 * that is the rule that keeps the ladder mutable outside the reconciler
 * (eslint `no-restricted-imports`, frontend/README conventions §1).
 *
 * `getSnapshot` returns the store's **version number**, never its state. React
 * requires the snapshot to be `Object.is`-stable between changes; returning the
 * state object would force every store to allocate a new one per update, which
 * for the book store is sixty times a second. So the version is the snapshot and
 * `store.state` is read directly during render — safe because the flush mutates
 * state and bumps the version in the same turn, before any listener runs.
 */

import { useSyncExternalStore } from 'react'
import type { ExternalStore } from '@/stores/store'

/** Subscribe to a store and read its live state. Re-renders once per `notify()`. */
export function useStore<T>(store: ExternalStore<T>): T {
  useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  return store.state
}

/** Subscribe without reading — for a component that renders from a ref or the DOM. */
export function useStoreVersion<T>(store: ExternalStore<T>): number {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
}

/**
 * Subscribe to **one primitive** read off a store, re-rendering only when that
 * value changes.
 *
 * For a reader that needs one field of a store whose other fields move much
 * faster: `<BookPanel>` follows the ticket's outcome, which changes on a click,
 * while the size beside it in the same store changes on every keystroke.
 * `useSyncExternalStore` compares snapshots with `Object.is`, so a primitive
 * selection is exactly as stable as the version number `useStore` uses.
 */
export function useStoreField<T, V extends string | number | boolean | bigint | null>(
  store: ExternalStore<T>,
  select: (state: T) => V,
): V {
  const read = () => select(store.state)
  return useSyncExternalStore(store.subscribe, read, read)
}
